import type { EntityId, ISODateTime } from "../domain/types.js";
import {
  classifyRecordingStorageError,
  MemoryRecordingBuffer,
  type BufferedAudioChunk,
  type RecordingBuffer,
  type RecordingBufferStatus,
  type RecordingChunkInput,
  type RecordingStorageErrorCode,
} from "./recording-buffer.js";

export type RecordingStorageStream = "source" | "analysis" | "buffer";

export interface RecordingStorageDegradation {
  readonly errorCode: RecordingStorageErrorCode;
  readonly stream: RecordingStorageStream;
  readonly sequence?: number;
}

/**
 * Keeps capture running when encrypted IndexedDB becomes unavailable.
 * The hook retains every unacknowledged wire packet separately; this class
 * supplies an in-memory recording buffer for the remainder of the open tab.
 */
export class CaptureBufferRuntime {
  #buffer: RecordingBuffer;
  readonly #retiredBuffers: RecordingBuffer[] = [];
  readonly #onDegraded: (failure: RecordingStorageDegradation) => void;
  #degraded: boolean;

  constructor(
    buffer: RecordingBuffer,
    onDegraded: (failure: RecordingStorageDegradation) => void,
  ) {
    this.#buffer = buffer;
    this.#onDegraded = onDegraded;
    const health = buffer.getHealth();
    this.#degraded = health.degraded;
    if (health.degraded) {
      this.#onDegraded({
        errorCode: health.errorCode ?? "unknown",
        stream: "buffer",
      });
    }
  }

  get degraded(): boolean {
    return this.#degraded;
  }

  #degrade(
    cause: unknown,
    stream: RecordingStorageStream,
    sequence?: number,
  ): void {
    if (!this.#degraded) {
      this.#degraded = true;
      this.#onDegraded({
        errorCode: classifyRecordingStorageError(cause),
        stream,
        ...(sequence === undefined ? {} : { sequence }),
      });
    }
    if (this.#buffer.getHealth().mode === "memory") return;
    this.#retiredBuffers.push(this.#buffer);
    this.#buffer = new MemoryRecordingBuffer();
  }

  async putChunk(
    input: RecordingChunkInput,
    stream: Exclude<RecordingStorageStream, "buffer">,
  ): Promise<void> {
    try {
      await this.#buffer.putChunk(input);
    } catch (cause) {
      this.#degrade(cause, stream, input.sequence);
      await this.#buffer.putChunk(input).catch(() => undefined);
    }
  }

  async listChunks(
    recordingId: EntityId,
    stream: Exclude<RecordingStorageStream, "buffer">,
  ): Promise<readonly BufferedAudioChunk[]> {
    try {
      return await this.#buffer.listChunks(recordingId);
    } catch (cause) {
      this.#degrade(cause, stream);
      return this.#buffer.listChunks(recordingId).catch(() => []);
    }
  }

  async markUploaded(
    recordingId: EntityId,
    sequence: number,
    uploadedAt: ISODateTime,
    stream: Exclude<RecordingStorageStream, "buffer">,
  ): Promise<void> {
    try {
      await this.#buffer.markUploaded(recordingId, sequence, uploadedAt);
    } catch (cause) {
      this.#degrade(cause, stream, sequence);
    }
  }

  async getStatus(
    recordingId: EntityId,
    stream: Exclude<RecordingStorageStream, "buffer">,
  ): Promise<RecordingBufferStatus> {
    try {
      return await this.#buffer.getStatus(recordingId);
    } catch (cause) {
      this.#degrade(cause, stream);
      return this.#buffer.getStatus(recordingId).catch(() => ({
        recordingId,
        chunkCount: 0,
        totalBytes: 0,
        pendingUploadCount: 0,
        safeThroughMs: 0,
        sequenceGaps: [],
      }));
    }
  }

  async clearRecording(recordingId: EntityId): Promise<void> {
    try {
      await this.#buffer.clearRecording(recordingId);
    } catch (cause) {
      this.#degrade(cause, "buffer");
      await this.#buffer.clearRecording(recordingId).catch(() => undefined);
    }
    for (const buffer of this.#retiredBuffers) {
      await buffer.clearRecording(recordingId).catch(() => undefined);
    }
  }

  async pruneRecordings(keepRecordingIds: readonly EntityId[]): Promise<number> {
    let removed = 0;
    try {
      removed += await this.#buffer.pruneRecordings(keepRecordingIds);
    } catch (cause) {
      this.#degrade(cause, "buffer");
    }
    for (const buffer of this.#retiredBuffers) {
      removed += await buffer.pruneRecordings(keepRecordingIds).catch(() => 0);
    }
    return removed;
  }

  close(): void {
    this.#buffer.close();
    for (const buffer of this.#retiredBuffers) buffer.close();
    this.#retiredBuffers.length = 0;
  }
}
